import { useState, useRef, useEffect } from 'react'
import {
  ImplementationStatus,
  IMPLEMENTATION_STATUS_META
} from '@models/index'
import './StatusDropdown.css'

interface StatusDropdownProps {
  value: ImplementationStatus
  onChange: (status: ImplementationStatus) => void
  disabled?: boolean
  size?: 'small' | 'medium'
}

const STATUS_ORDER: ImplementationStatus[] = [
  'not_started',
  'planning',
  'in_progress',
  'completed',
  'blocked',
  'not_applicable'
]

export function StatusDropdown({
  value,
  onChange,
  disabled = false,
  size = 'small'
}: StatusDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close on escape key
  useEffect(() => {
    function handleEscKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey)
      return () => document.removeEventListener('keydown', handleEscKey)
    }
  }, [isOpen])

  const currentMeta = IMPLEMENTATION_STATUS_META[value]

  const handleSelect = (status: ImplementationStatus) => {
    onChange(status)
    setIsOpen(false)
  }

  return (
    <div
      ref={dropdownRef}
      className={`status-dropdown ${size} ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
    >
      <button
        type="button"
        className="status-dropdown-trigger"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Status: ${currentMeta.label}`}
        style={{ '--status-color': currentMeta.color } as React.CSSProperties}
      >
        <span className="status-icon">{currentMeta.icon}</span>
        <span className="status-label">{currentMeta.label}</span>
        <span className="dropdown-chevron">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <ul className="status-dropdown-menu" role="listbox">
          {STATUS_ORDER.map(status => {
            const meta = IMPLEMENTATION_STATUS_META[status]
            const isSelected = status === value

            return (
              <li
                key={status}
                role="option"
                aria-selected={isSelected}
                className={`status-option ${isSelected ? 'selected' : ''}`}
                onClick={() => handleSelect(status)}
                style={{ '--status-color': meta.color } as React.CSSProperties}
              >
                <span className="status-icon">{meta.icon}</span>
                <div className="status-option-content">
                  <span className="status-label">{meta.label}</span>
                  <span className="status-description">{meta.description}</span>
                </div>
                {isSelected && <span className="check-icon">✓</span>}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default StatusDropdown
